import React, { useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Transforms, Editor, Path, Range } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { computed } from 'nanostores';
import { useStore } from '@nanostores/react';

import * as serializer from 'basic-editor-value-serializer';

import application from 'application';
import internalEventCodes from 'event-system/codes/internal';
import editorHandles from 'constants/editor-handles';

import { getStyles, getStylesV2 } from 'generics/components/basic-text-editor-slate/utilities';

import Helpers from '../../helpers';
import Resizable from '../resizable';

import {
  tryInitializeDraggability,
  tryMakeElementDroppable
} from './drag-and-drop';

import {
  determineWrapperStyles,
  determineStyleHostClass
} from './utilities';

import { ReactComponent as NodeOptionsControlIcon } from 'assets/images/node-options-control-icon.svg';
import { ensureEditorStores, keyFromPath, SYM_OPEN } from '../../stores/active-block';

import './index.scss';

const OPEN_MENU = Symbol.for('vz.openNodeMenu');
const CLOSE_MENU = Symbol.for('vz.closeNodeMenu');

function BaseElement(props) {

  const element = props.element;

  const editor = useSlateStatic();

  const mainReference = useRef(null);
  const nodeHandleContainerReference = useRef(null);
  const nodeStyleOptionsOpenerReference = useRef(null);
  const removeDraggabilityReference = useRef(null);
  const editorElementReference = useRef(null);
  const elementPathRef = useRef(null);

  const elementPath = ReactEditor.findPath(editor, element);

  const getPath = useCallback(function _getPath() {
    return elementPathRef.current?.current ?? ReactEditor.findPath(editor, element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, element]);

  const { activeBlockKey, selectedBlockKeys, nodeMenu } = ensureEditorStores(editor);

  const isHighlightedStore = useMemo(function buildHighlightedStore() {
    return computed([activeBlockKey, nodeMenu], function computeIsHighlighted(activeKey, menu) {
      return menu.isOpen && activeKey === keyFromPath(elementPath);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBlockKey, nodeMenu, keyFromPath(elementPath)]);

  const isHighlighted = useStore(isHighlightedStore);

  const isDirectlySelectedStore = useMemo(function buildDirectlySelectedStore() {
    return computed([selectedBlockKeys, nodeMenu], function computeIsDirectlySelected(keys, menu) {
      if (menu.isOpen === true) {
        return false;
      }
      return keys.indexOf(keyFromPath(elementPath)) !== -1;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlockKeys, nodeMenu, keyFromPath(elementPath)]);

  const isDirectlySelected = useStore(isDirectlySelectedStore);

  useEffect(function onLoad() {

    return initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onLoad() {

    const elementPath = ReactEditor.findPath(editor, element);
    elementPathRef.current?.unref();
    elementPathRef.current = Editor.pathRef(editor, elementPath, { affinity: 'backward' });
    return function onUnload() {
      elementPathRef.current?.unref();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, element]);

  useEffect(function tryShowDragMenuOnLoad() {

    if (props.dragMenuOnByDefault !== true) {
      return;
    }

    var isFirstElement = elementPath.length === 1 && elementPath[0] === 0;

    if (isFirstElement !== true) {
      return;
    }

    setTimeout(function showMenuAfterRender() {
      handleNodeStyleOptionsOpenerControlClick();
    }, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialize = useCallback(function initialize() {

    const undoDroppable = tryMakeElementDroppable(editor, mainReference.current, {
      node: element,
      getPath
    });
    // positionNodeOptionsOpener(nodeStyleOptionsOpenerReference.current);

    return function onUnload() {
      undoDroppable();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, element, getPath()]);

  const getEditorElement = useCallback(function getEditorElement(nodeOptionsOpener) {

    return nodeOptionsOpener.parentNode.parentNode.querySelector('.editor-element');

  }, []);

  const handleNodeStyleOptionsOpenerControlClick = useCallback(function handleNodeStyleOptionsOpenerControlClick() {

    const nodeOptionsOpener = nodeStyleOptionsOpenerReference.current;
    const editorElement = getEditorElement(nodeOptionsOpener);

    let anchorRectangle = nodeOptionsOpener.getBoundingClientRect();

    anchorRectangle = JSON.parse(JSON.stringify(anchorRectangle));

    anchorRectangle.left += 20;
    anchorRectangle.height = editorElement.getBoundingClientRect().height;

    editor[OPEN_MENU]({
      anchorRectangle,
      getPath,
      nodeRoot: mainReference.current,
      customNodeControls: props.customNodeControls || [],
      disabledNodeControls: props.disabledNodeControls || []
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEditorElement, getPath(), props]);

  const isPathEquals = useCallback(function isPathEquals(activeElementPath, elementPath) {

    if (typeof activeElementPath === 'undefined') {
      return false;
    }

    if (activeElementPath === null) {
      return false;
    }

    return Path.equals(activeElementPath, elementPath);

  }, []);

  const handleKeyDownEvent = useCallback(function handleKeyDownEvent(event) {

    const commandKeyPressed = event.metaKey === true || event.ctrlKey === true;
    const pressedKey = event.key.toLowerCase();

    if (commandKeyPressed === true && pressedKey === 'c' && event.shiftKey === true) {
      event.preventDefault();
      Helpers.copyPaste.copyBlockStyles(editor, getPath());
      return;
    }

    if (commandKeyPressed === true && pressedKey === 'v' && event.shiftKey === true) {
      event.preventDefault();
      Helpers.copyPaste.pasteBlockStyles(editor, getPath());
      return;
    }

    if (commandKeyPressed === true && pressedKey === 'c') {
      event.preventDefault();
      Helpers.copyPaste.copyBlocks(editor, [getPath()]);
      return;
    }

    if (['Backspace', 'Del'].indexOf(event.key) === -1) {
      return;
    }

    event.preventDefault();
    editor[CLOSE_MENU]({
      getPath,
    });
    Helpers.deleteNodeByPath(editor, elementPath);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, elementPath, getPath(), props]);

  const enableDraggability = useCallback(function enableDraggability() {

    const removeDraggability = removeDraggabilityReference.current;

    if (removeDraggability !== null) {
      removeDraggability();
    }

    removeDraggabilityReference.current = tryInitializeDraggability(editor, {
      element: mainReference.current,
      getPath
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, getPath()]);

  const disableDraggability = useCallback(function disableDraggability() {

    const removeDraggability = removeDraggabilityReference.current;

    if (removeDraggability === null) {
      return;
    }

    removeDraggability();

    removeDraggabilityReference.current = null;

  }, []);

  const handleMouseDown = useCallback((event) => {

    if (props.contentEditable === false) {
      const blockRange = Editor.range(editor, elementPath);

      // Get current selection
      const { selection } = editor;

      // If selection is partial, select the entire block
      if (!Range.equals(selection, blockRange)) {
        Transforms.select(editor, blockRange);
        event.preventDefault(); // Prevent default to ensure full block is selected
        event.stopPropagation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementPath]);

  function renderNodeHandle() {

    if (props.enableAdvancedOptions !== true) {
      return;
    }

    const containerAttributes = {
      className: 'node-handles-container',
      style: {},
      contentEditable: false,
      ref: nodeHandleContainerReference
    };

    const nodeStyleOptionsControlAttributes = {
      type: 'button',
      className: 'editor-node-control-button',
      ref: nodeStyleOptionsOpenerReference,
      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (props.enabledDragAndDropOnly === true) {
          return;
        }
        setTimeout(function someTimeLater() {
          handleNodeStyleOptionsOpenerControlClick();
          if (typeof props.onSelectChange === 'function') {
            props.onSelectChange(elementPath);
          }
        }, 10);
        return false;
      },
      onKeyDown(event) {
        handleKeyDownEvent(event);
      },
      onMouseEnter(event) {
        enableDraggability(event);
      },
      onMouseLeave(event) {
        disableDraggability(event);
      }
    };

    return <React.Fragment>
      <div {...containerAttributes}>

        <button {...nodeStyleOptionsControlAttributes}>
          <NodeOptionsControlIcon />
        </button>

      </div>
    </React.Fragment>;

  }

  function renderEditorElement(style) {

    const editorElementProperties = {
      className: 'editor-element hide-scrollbar',
      style
    };

    if (element.resizable !== true) {
      editorElementProperties.ref = editorElementReference;
    }

    return <div {...editorElementProperties}>
      {props.children}
    </div>;

  }

  function _renderEditorElement() {

    const style = {};

    if (element.resizable !== true) {
      return renderEditorElement(style);
    }

    const resizableProperties = {
      editor,
      style,
      elementPath
    };

    return <Resizable ref={editorElementReference} {...resizableProperties}>
      {renderEditorElement({})}
    </Resizable>;

  }

  function renderMain() {

    const mainAttributes = {
      className: 'editor-node-main ' + (props.className || ''),
      ref: mainReference,
      style: determineWrapperStyles(element),
      onDoubleClick: handleMouseDown,
      'data-node-id': element.properties?.id
    };
    const hoverStyle = serializer.stylers.generateStyleBlock(serializer.stylers.getNodeStyles(element), element, determineStyleHostClass(element)).block.replaceAll('<style>', '').replaceAll('</style>', '');

    if (props.enableAdvancedOptions === true) {
      mainAttributes.className += ' with-advanced-options';
    }

    if (isPathEquals(props.activeElementPath.current, elementPath) === true) {
      mainAttributes.className += ' with-advanced-options display-outline';

      if (props.activeHandle === editorHandles.NODE_MENU) {
        mainAttributes.className += ' display-drag-handle';
      } else if (props.activeHandle === editorHandles.MORE_NODE_ACTIONS_MENU) {
        mainAttributes.className += ' display-drag-handle';
      }

    }

    if (isHighlighted === true) {
      mainAttributes.className += ' highlight';
    }

    if (Helpers.isNodeActive(editor, elementPath) === true) {
      mainAttributes.className += ' active';
    }

    if (Helpers.isNodeSelected(editor, element) === true) {
      mainAttributes.className += ' selected';
    }

    if (isDirectlySelected === true) {
      mainAttributes.className += ' directly-selected';
    }

    return <div {...mainAttributes}>
      <style>{hoverStyle}</style>
      {renderNodeHandle()}
      {_renderEditorElement()}
    </div>;

  }

  function isBlockAiThinking() {
    const properties = element.properties;
    if (!properties || properties.aiThinking !== true) {
      return false;
    }
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return properties.aiThinkingStartedAt > fiveMinutesAgo;
  }

  return renderMain();

}

export default React.memo(BaseElement);